import { Stack, useTheme } from "@mui/material";
import { useMemo } from "react";
import { ExpenseAmountFormSheet } from "web_myplann/Finances/Components/ExpenseAmountFormSheet";
import { ExpenseCategoryList } from "web_myplann/Finances/Components/ExpenseCategoryList";
import { FinancesStepLayout } from "web_myplann/Finances/Components/FinancesStepLayout";
import { LeaveFinancesDrawer } from "web_myplann/Finances/Components/LeaveFinancesDrawer";
import { SubcategoryPickerDrawer } from "web_myplann/Finances/Components/SubcategoryPickerDrawer";
import { useExpenseV2Form } from "web_myplann/Finances/Forms/useExpenseV2Form";
import { groupExpensesByType } from "web_myplann/Finances/Helpers/groupExpensesByType";
import { useExpenseEditor } from "web_myplann/Finances/Hooks/useExpenseEditor";
import { useFinancesWizardNavigation } from "web_myplann/Finances/Hooks/useFinancesWizardNavigation";
import { useLeaveFinancesGuard } from "web_myplann/Finances/Hooks/useLeaveFinancesGuard";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

const R = Resources.Finances;

export const ExpensesV2Page = () => {
	const { t } = useResource();
	const theme = useTheme();
	const { goToStep } = useFinancesWizardNavigation();

	const {
		expenses,
		expensesReplace,
		removeExpense,
		setValue,
		onSubmit,
		exact,
		total,
		setExact,
		setManualTotal,
		hasUnconfirmedChanges,
	} = useExpenseV2Form();

	const editor = useExpenseEditor({ expenses, expensesReplace, setValue });
	const { isLeaving, stay, leave } = useLeaveFinancesGuard(
		hasUnconfirmedChanges,
	);
	const groups = useMemo(() => groupExpensesByType(expenses), [expenses]);

	const handleContinue = async () => {
		await onSubmit();
		goToStep(Routing.Finances.Overview.path());
	};

	const handleBack = async () => {
		await onSubmit();
		goToStep(Routing.Finances.Income.path());
	};

	return (
		<>
			<FinancesStepLayout
				onBack={handleBack}
				description={t(R.Expenses.descriptionTentative)}
				tentativeLabel={t(R.Expenses.titleTentatively)}
				exactLabel={t(R.Expenses.titleExact)}
				exact={exact}
				hasItems={expenses.length > 0}
				onExactChange={setExact}
				total={total}
				onTotalChange={setManualTotal}
				whatToCount="expenses"
				continueLabel={t(Resources.Common.continue)}
				onContinue={handleContinue}
			>
				<Stack mt={theme.spacingTokens.sm} gap={theme.spacingTokens.sm}>
					<ExpenseCategoryList
						groups={groups}
						onAdd={editor.openPicker}
						onEdit={editor.openEdit}
						onRemove={removeExpense}
					/>
				</Stack>
			</FinancesStepLayout>

			<SubcategoryPickerDrawer
				type={editor.pickerType}
				availableNames={editor.availableNames}
				onSelect={editor.selectName}
				onClose={editor.closePicker}
			/>
			<ExpenseAmountFormSheet
				target={editor.amountTarget}
				isEditing={editor.isEditing}
				initialValues={editor.initialValues}
				onClose={editor.closeAmount}
				onSubmit={editor.submitAmount}
			/>
			<LeaveFinancesDrawer open={isLeaving} onStay={stay} onLeave={leave} />
		</>
	);
};
